import { z } from 'zod';
import { Dateish, IdCoerce } from './common';

// ── Sincronização TiFlux → ERP (DESIGN-V12) ─────────────────────────────────
// Espelho de leitura em direção ÚNICA: o ERP roda em paralelo ao TiFlux com os
// dados reais da conta. O token (`Basic ...`) NUNCA volta inteiro pela API —
// padrão maskApiKey das demais integrações.

export const TIFLUX_DEFAULT_ENDPOINT = 'https://api.tiflux.com/api/v1';

/** intervalo do sync automático em minutos (PUT valida 5..1440) */
export const TIFLUX_MIN_INTERVAL_MINUTES = 5;
export const TIFLUX_MAX_INTERVAL_MINUTES = 1440;
export const TIFLUX_DEFAULT_INTERVAL_MINUTES = 30;

/**
 * entidades sincronizadas por rodada, na ordem de execução.
 *
 * v13 acrescentou `users`/`groups`/`entities` (fases próprias, ANTES dos
 * tickets — o responsável do ticket resolve pelo link de usuário, então quem
 * cria esse link tem de rodar antes) e `appointments` (por ticket, na fase B,
 * junto do detalhe/comentários).
 */
export const TifluxSyncEntitySchema = z.enum([
  'desks',
  'clients',
  'users',
  'groups',
  'entities',
  'tickets',
  'comments',
  'appointments',
  'round',
]);
export type TifluxSyncEntity = z.infer<typeof TifluxSyncEntitySchema>;

export const TifluxSyncErrorSchema = z.object({
  entity: TifluxSyncEntitySchema,
  /** id TiFlux da entidade que falhou (ticket_number, client id, …) */
  id: z.number().optional(),
  message: z.string(),
});
export type TifluxSyncError = z.infer<typeof TifluxSyncErrorSchema>;

/** cap de erros gravados por rodada (o restante é descartado com contagem) */
export const TIFLUX_MAX_ERRORS = 50;

/**
 * resultado da última rodada, gravado em tiflux_integrations.last_result.
 *
 * Os três últimos contadores são o PROGRESSO do backfill em duas fases (ver
 * DESIGN-V12): a fase A ("cobertura") espelha 100% dos tickets a partir do
 * payload de LISTA em ~29 requisições, e a fase B ("enriquecimento") busca
 * descrição e comentários ticket a ticket, em fatias por rodada — o acervo
 * inteiro não cabe num round de 10min a ~4 req/s. `pendingEnrich` é o que
 * falta enriquecer; zero = backfill concluído.
 *
 * Têm `.default(0)`: um last_result gravado por uma versão anterior continua
 * fazendo parse (o toRow do repositório é defensivo, mas não precisa entrar
 * em ação por causa de um campo novo).
 */
export const TifluxSyncResultSchema = z.object({
  ok: z.boolean(),
  counts: z.object({
    desks: z.number(),
    clients: z.number(),
    tickets: z.number(),
    comments: z.number(),
    /**
     * clientes criados como ESBOÇO a partir do `client:{id,name}` embutido no
     * ticket, porque o cadastro (`GET /clients`) não os devolve mais — cliente
     * removido do registro no TiFlux com ticket histórico ainda apontando para
     * ele. Nascem inativos e com o nome embutido; se um dia o cadastro voltar
     * a listá-los, o link faz o upsert normal enriquecê-los NO LUGAR.
     */
    clientsPlaceholder: z.number().default(0),
    /** tickets que ganharam descrição + comentários NESTA rodada */
    enriched: z.number().default(0),
    /** tickets que ainda esperam enriquecimento (0 = backfill concluído) */
    pendingEnrich: z.number().default(0),
    /** total de tickets da conta (header x-total-tickets: abertos + fechados) */
    totalTickets: z.number().default(0),
    // ── v13 ────────────────────────────────────────────────────────────────
    /**
     * usuários espelhados de `/users`. Inclui os que só ganharam VÍNCULO: um
     * e-mail que já existia no ERP (a equipe real) tem prioridade e não é
     * sobrescrito — só passa a resolver como responsável de ticket.
     */
    users: z.number().default(0),
    /**
     * Usuários de CLIENTE (portal) pulados: `/users` mistura equipe com
     * contatos de cliente, e na conta real 129 dos 170 são portal. Espelhá-los
     * poluiria a tela de Usuários e todo seletor de responsável com gente que
     * nunca poderia ser responsável por um ticket.
     *
     * Contador, não erro: é a composição normal da conta, idêntica toda rodada.
     */
    usersSkipped: z.number().default(0),
    /** grupos de atendentes espelhados de `/technical_groups` */
    groups: z.number().default(0),
    /** apontamentos espelhados nesta rodada (fase B, por ticket) */
    appointments: z.number().default(0),
    /** grupos de campos personalizados espelhados de `/entities` */
    entities: z.number().default(0),
    /**
     * Entidades que a API NÃO permite classificar: não voltam em nenhum
     * `?applied_in=` (ticket|client|equipment), e o nosso
     * `entity_groups.applied_in` é NOT NULL. Na conta real são 4 fixas (as
     * "Campo personalizado …").
     *
     * É CONTADOR, não erro, de propósito: o resultado é o mesmo em toda
     * rodada, então registrá-lo como erro deixaria o card permanentemente
     * vermelho e treinaria todo mundo a ignorá-lo. Erro tem de significar
     * "algo quebrou e alguém precisa agir". Quem quiser investigar tem o log
     * da rodada, que nomeia as entidades puladas.
     */
    entitiesSkipped: z.number().default(0),
    // ── spec 011 ───────────────────────────────────────────────────────────
    /**
     * Tickets que o TiFlux trouxe e o espelho IGNOROU porque a mesa local (ou
     * a de destino no payload) é operada no ERP. Contador, não erro: é a chave
     * da migração funcionando. Logo depois do deploy tem de ser 0 — as mesas
     * da PoolTrack nascem `tiflux`; um número aqui sem ninguém ter virado mesa
     * é sinal de backfill errado.
     */
    ticketsSkippedErpDesk: z.number().default(0),
    // ── spec 024 ───────────────────────────────────────────────────────────
    /**
     * Tickets que o TiFlux trouxe e o espelho IGNOROU porque estão FORA DO
     * ESPELHO (cliente operado no ERP, ou ticket desligado sozinho) — em
     * qualquer fase, e contados uma vez por ticket. Contador próprio: não se
     * confunde com o da mesa do ERP. Enquanto nenhum cliente for desligado, 0;
     * na primeira rodada depois de desligar a Minas Prime, o número de tickets
     * dela que a rodada viu (SC-001).
     */
    ticketsSkippedDetached: z.number().default(0),
    // ── spec 026 ───────────────────────────────────────────────────────────
    /**
     * Tickets de cliente OPERADO NO ERP que saíram do espelho nesta rodada,
     * logo depois do primeiro detalhe completo (ficha, solicitante,
     * comentários e apontamentos): o motivo `arrived_operated`. É o ticket
     * novo que o worker do DBOS ainda cria no TiFlux para a Minas Prime.
     */
    ticketsAdopted: z.number().default(0),
    // ── spec 043 ───────────────────────────────────────────────────────────
    /**
     * Espelhados abertos no ERP que NÃO estão na lista de abertos do TiFlux,
     * conferidos um a um pelo DETALHE (`GET /tickets/:n`) nesta rodada. A lista
     * de fechados da v1 só traz os recentes, então o fechado há mais tempo não
     * aparece em lista nenhuma — é o detalhe que diz o que ele é lá:
     * fechado (fecha aqui), cancelado (cancela), apagado/404 (cancela, com o
     * motivo no histórico) ou ainda aberto (nada: a lista atrasou).
     */
    reconciledClosed: z.number().default(0),
    reconciledCanceled: z.number().default(0),
    reconciledDeleted: z.number().default(0),
    reconciledStillOpen: z.number().default(0),
    /**
     * Quantos espelhados abertos seguem FORA da lista de abertos depois desta
     * rodada — a dívida da fase C, que anda em fatias. Depois de drenada, fica
     * perto de zero (o que sobra são os que seguem abertos lá).
     */
    pendingReconcile: z.number().default(0),
  }),
  errors: z.array(TifluxSyncErrorSchema),
  durationMs: z.number(),
});
export type TifluxSyncResult = z.infer<typeof TifluxSyncResultSchema>;

/**
 * `source` do evento `tiflux_closed` (spec 043): o fechamento veio do espelho,
 * não de uma pessoa nem de automação. A tela do histórico e o adapter do espelho
 * leem a mesma constante.
 */
export const TIFLUX_MIRROR_EVENT_SOURCE = 'tiflux_mirror';

/** o que o espelho viu no TiFlux ao encerrar o ticket aqui (payload `outcome`) */
export const TifluxClosureOutcomeSchema = z.enum(['closed', 'canceled', 'deleted']);
export type TifluxClosureOutcome = z.infer<typeof TifluxClosureOutcomeSchema>;

// ── Cliente operado no ERP: ciclo próprio e "Puxar do TiFlux" (spec 026) ────
// A Minas Prime vive nos dois lados: o worker do DBOS segue criando o ticket
// no TiFlux, e a equipe de lá ainda pode mexer nele. O ERP traz o ticket novo
// inteiro e o ADOTA (sai do espelho no primeiro detalhe completo); um ciclo
// curto, só destes clientes, traz o que é aditivo (comentários, apontamentos) e
// DETECTA, sem aplicar, o que conflita (etapa, status, responsável, mesa) — a
// pendência. O admin decide o que puxar. Nada é escrito no TiFlux.

/** intervalo do ciclo dos clientes operados, em minutos (PUT valida 5..1440) */
export const TIFLUX_OPERATED_DEFAULT_INTERVAL_MINUTES = 10;

/** os campos que o ciclo compara e o "Puxar do TiFlux" aplica, campo a campo */
export const TIFLUX_PULL_FIELDS = ['stage', 'status', 'responsible', 'desk'] as const;
export const TifluxPullFieldSchema = z.enum(TIFLUX_PULL_FIELDS);
export type TifluxPullField = z.infer<typeof TifluxPullFieldSchema>;

export const TIFLUX_PULL_FIELD_LABELS: Record<TifluxPullField, string> = {
  stage: 'Etapa',
  status: 'Status',
  responsible: 'Responsável',
  desk: 'Mesa',
};

/**
 * Um lado de um campo. `key` é o que se COMPARA: o id LOCAL da etapa, da mesa
 * ou do responsável (o do TiFlux chega resolvido pelo vínculo — nunca por nome
 * nem por número de ticket) e, no status, `open` | `closed` | `canceled`. No
 * responsável, `key` null é "sem responsável". `label` é o que a tela mostra.
 *
 * O TiFlux v1 só diz aberto ou fechado (`is_closed`): o `closed` de lá casa com
 * fechado OU cancelado no ERP.
 */
export const TifluxFieldValueSchema = z.object({
  key: z.string().nullable(),
  label: z.string().nullable(),
});
export type TifluxFieldValue = z.infer<typeof TifluxFieldValueSchema>;

/**
 * Resultado da última volta do ciclo dos operados, gravado em
 * tiflux_integrations.operated_last_result. Todos os contadores têm default: um
 * resultado gravado por uma versão anterior continua fazendo parse.
 */
export const TifluxOperatedResultSchema = z.object({
  ok: z.boolean(),
  counts: z.object({
    /** clientes operados percorridos nesta volta */
    clients: z.number().default(0),
    /** tickets desses clientes que as listas do TiFlux devolveram */
    listed: z.number().default(0),
    /** tickets novos que chegaram pelo TiFlux e foram importados */
    created: z.number().default(0),
    /** tickets que saíram do espelho no primeiro detalhe completo */
    adopted: z.number().default(0),
    /** tickets fora do espelho que mudaram lá e tiveram comentários e apontamentos conferidos */
    checked: z.number().default(0),
    /** comentários novos do TiFlux inseridos */
    comments: z.number().default(0),
    /** apontamentos inseridos ou atualizados pelo vínculo */
    appointments: z.number().default(0),
    /** pendências criadas ou atualizadas (mudou lá, e o ERP tem outro valor) */
    pendingsOpened: z.number().default(0),
    /** pendências removidas porque os valores voltaram a bater */
    pendingsResolved: z.number().default(0),
    /**
     * ADR-013: apontamentos editados no ERP que o TiFlux tem diferente e a
     * volta NÃO regravou (o ERP vence) — os pulados calados e os que viraram
     * pendência
     */
    appointmentsErpKept: z.number().default(0),
    /** ADR-013: pendências de apontamento criadas (o TiFlux mudou depois da edição no ERP) */
    appointmentPendingsOpened: z.number().default(0),
    /** ADR-013: pendências de apontamento removidas porque os dois lados voltaram a bater */
    appointmentPendingsResolved: z.number().default(0),
    /** tickets com vínculo que o TiFlux não devolveu mais (404) */
    missing: z.number().default(0),
    /**
     * 1 quando a API ignorou o filtro `client_id` (devolveu tickets de outros
     * clientes) e a volta caiu para as listas completas, filtrando aqui
     */
    clientFilterIgnored: z.number().default(0),
    /** chamadas feitas ao TiFlux nesta volta — todas GET */
    calls: z.number().default(0),
  }),
  errors: z.array(TifluxSyncErrorSchema),
  durationMs: z.number(),
});
export type TifluxOperatedResult = z.infer<typeof TifluxOperatedResultSchema>;

/**
 * A pendência de sincronização de UM campo de UM ticket (FR-003): mudou no
 * TiFlux e o ERP tem outro valor. Nada no ERP muda sozinho; ela sai quando os
 * valores voltam a bater ou quando o admin a resolve no "Puxar do TiFlux".
 * GET /tickets/:n/tiflux-pendings — o indicador do detalhe do ticket.
 */
export const TifluxFieldPendingSchema = z.object({
  field: TifluxPullFieldSchema,
  tiflux: TifluxFieldValueSchema,
  erp: TifluxFieldValueSchema,
  /** a primeira vez que o ciclo viu a diferença */
  createdAt: Dateish,
  /** a última volta que a confirmou */
  seenAt: Dateish,
});
export type TifluxFieldPending = z.infer<typeof TifluxFieldPendingSchema>;

/**
 * Um apontamento como um dos lados o descreve — os quatro valores que o
 * espelho compara (autor, início, fim, descrição). `userName` é o que a tela
 * mostra, gravado junto como o `label` dos campos.
 */
export const TifluxAppointmentValueSchema = z.object({
  userId: z.number().int(),
  userName: z.string().nullable(),
  startedAt: Dateish,
  endedAt: Dateish,
  description: z.string().nullable(),
});
export type TifluxAppointmentValue = z.infer<typeof TifluxAppointmentValueSchema>;

/**
 * Pendência de APONTAMENTO (ADR-013, 03/10): o apontamento foi editado no ERP
 * — e o ERP vence — mas o TiFlux mudou esse mesmo apontamento DEPOIS da
 * edição. Nada é sobrescrito; uma pessoa decide no "Puxar do TiFlux" entre
 * manter o do ERP e aceitar o do TiFlux. Uma por apontamento. `erp` é o valor
 * de AGORA no ERP (lido na hora), `tiflux` o que o ciclo viu lá.
 */
export const TifluxAppointmentPendingSchema = z.object({
  field: z.literal('appointment'),
  /** o apontamento LOCAL */
  appointmentId: z.number().int(),
  tiflux: TifluxAppointmentValueSchema,
  erp: TifluxAppointmentValueSchema,
  createdAt: Dateish,
  seenAt: Dateish,
});
export type TifluxAppointmentPending = z.infer<typeof TifluxAppointmentPendingSchema>;

/** GET /tickets/:n/tiflux-pendings — as dos quatro campos e as de apontamento */
export const TifluxTicketPendingSchema = z.union([
  TifluxFieldPendingSchema,
  TifluxAppointmentPendingSchema,
]);
export type TifluxTicketPending = z.infer<typeof TifluxTicketPendingSchema>;

/** um campo que difere, na prévia do "Puxar do TiFlux" */
export const TifluxPullFieldDiffSchema = z.object({
  field: TifluxPullFieldSchema,
  tiflux: TifluxFieldValueSchema,
  erp: TifluxFieldValueSchema,
  /** o ERP mudou este campo depois que o ticket saiu do espelho (fora o que veio de um "Puxar") */
  erpChangedSinceDetach: z.boolean(),
  /** a pendência gravada pelo ciclo, se houver: desde quando */
  pendingSince: Dateish.nullable(),
  /** false = não dá para aplicar agora (ver `blockedReason`) */
  applicable: z.boolean(),
  blockedReason: z.string().nullable(),
  /**
   * Campos que precisam ser marcados JUNTO: a etapa de outra mesa só entra com
   * a mesa; mexer em ticket fechado no ERP só com o status reabrindo.
   */
  requires: z.array(TifluxPullFieldSchema),
});
export type TifluxPullFieldDiff = z.infer<typeof TifluxPullFieldDiffSchema>;

export const TifluxPullCommentSchema = z.object({
  tifluxId: z.number().int(),
  author: z.string().nullable(),
  createdAt: Dateish.nullable(),
  /** começo do texto — a íntegra entra ao aplicar */
  excerpt: z.string(),
});
export type TifluxPullComment = z.infer<typeof TifluxPullCommentSchema>;

export const TifluxPullAppointmentSchema = z.object({
  tifluxId: z.number().int(),
  user: z.string().nullable(),
  startedAt: Dateish,
  endedAt: Dateish,
  description: z.string().nullable(),
  /** novo = sem vínculo; changed = vinculado, e o TiFlux mudou */
  change: z.enum(['new', 'changed']),
});
export type TifluxPullAppointment = z.infer<typeof TifluxPullAppointmentSchema>;

/**
 * Apontamento editado no ERP que o TiFlux mudou DEPOIS da edição (ADR-013): o
 * ERP vence, então nada entra sozinho — o admin escolhe entre manter o do ERP
 * e aceitar o do TiFlux. Não marcado = fica o do ERP, e a pendência sai (foi
 * decidido), como nos campos.
 */
export const TifluxPullAppointmentConflictSchema = z.object({
  /** o apontamento LOCAL */
  appointmentId: z.number().int(),
  tifluxId: z.number().int(),
  tiflux: TifluxAppointmentValueSchema,
  erp: TifluxAppointmentValueSchema,
  /** o valor do TiFlux que a prévia mostrou, em chave: o aplicar o confere (stale) */
  tifluxKey: z.string(),
  /** a última edição no ERP */
  erpEditedAt: Dateish.nullable(),
  erpEditedBy: z.string().nullable(),
  /** a pendência gravada pelo ciclo, se houver: desde quando */
  pendingSince: Dateish.nullable(),
});
export type TifluxPullAppointmentConflict = z.infer<typeof TifluxPullAppointmentConflictSchema>;

/** um ticket da prévia — só entra quem tem diferença */
export const TifluxPullTicketSchema = z.object({
  /** número LOCAL (o do ERP) */
  ticketNumber: z.number().int(),
  /** número no TiFlux, pelo vínculo — nunca o mesmo espaço do local */
  tifluxTicketNumber: z.number().int(),
  title: z.string(),
  comments: z.array(TifluxPullCommentSchema),
  appointments: z.array(TifluxPullAppointmentSchema),
  /** editados no ERP e mudados no TiFlux depois — manter ou aceitar (ADR-013) */
  appointmentConflicts: z.array(TifluxPullAppointmentConflictSchema),
  fields: z.array(TifluxPullFieldDiffSchema),
});
export type TifluxPullTicket = z.infer<typeof TifluxPullTicketSchema>;

/** GET /tiflux/erp-clients/:clientId/pull — a prévia, buscada no TiFlux na hora */
export const TifluxPullPreviewSchema = z.object({
  client: z.object({ id: z.number().int(), name: z.string() }),
  tifluxClientIds: z.array(z.number().int()),
  checkedAt: Dateish,
  tickets: z.array(TifluxPullTicketSchema),
  /** tickets que a prévia não conseguiu conferir a tempo (o teto da consulta) */
  unchecked: z.array(z.number().int()),
  /** avisos da consulta (lista que falhou, filtro ignorado…) */
  warnings: z.array(z.string()),
});
export type TifluxPullPreview = z.infer<typeof TifluxPullPreviewSchema>;

/**
 * POST /tiflux/erp-clients/:clientId/pull — aplicar. Por ticket, os campos que o
 * admin VIU na prévia, com o valor do TiFlux que viu (`tifluxKey`) e se quer
 * puxá-lo (`apply`). Comentários e apontamentos entram sempre. Campo não
 * marcado fica como está no ERP, e a pendência dele sai (foi decidido). Se o
 * TiFlux mudou de novo depois da prévia, o campo não é aplicado (`stale`).
 */
export const TifluxPullApplyBodySchema = z
  .object({
    tickets: z
      .array(
        z
          .object({
            ticketNumber: IdCoerce,
            fields: z
              .array(
                z
                  .object({
                    field: TifluxPullFieldSchema,
                    tifluxKey: z.string().nullable(),
                    apply: z.boolean(),
                  })
                  .strict()
              )
              .max(TIFLUX_PULL_FIELDS.length),
            /**
             * ADR-013: os apontamentos em conflito que o admin VIU, com a
             * chave do valor do TiFlux que viu e se aceita o de lá (`accept`).
             * Não aceito = fica o do ERP, e a pendência sai. Se o TiFlux mudou
             * de novo depois da prévia, nada muda (`stale`). Ausente = nenhum.
             */
            appointments: z
              .array(
                z
                  .object({
                    appointmentId: IdCoerce,
                    tifluxKey: z.string().max(64),
                    accept: z.boolean(),
                  })
                  .strict()
              )
              .max(500)
              .optional(),
          })
          .strict()
      )
      .min(1)
      .max(500),
  })
  .strict();
export type TifluxPullApplyBody = z.infer<typeof TifluxPullApplyBodySchema>;

export const TifluxPullAppliedChangeSchema = z.object({
  field: TifluxPullFieldSchema,
  from: z.string().nullable(),
  to: z.string().nullable(),
});

export const TifluxPullTicketResultSchema = z.object({
  ticketNumber: z.number().int(),
  applied: z.array(TifluxPullAppliedChangeSchema),
  /** vistos e mantidos no ERP (a pendência saiu) */
  kept: z.array(TifluxPullFieldSchema),
  /** o TiFlux mudou de novo depois da prévia: não aplicado, refaça a prévia */
  stale: z.array(TifluxPullFieldSchema),
  failed: z.array(z.object({ field: TifluxPullFieldSchema, error: z.string() })),
  comments: z.number().int(),
  appointments: z.object({
    inserted: z.number().int(),
    updated: z.number().int(),
    /** ADR-013: conflitos em que o admin aceitou o valor do TiFlux */
    accepted: z.number().int().default(0),
    /** ADR-013: conflitos em que ficou o do ERP (a pendência saiu) */
    kept: z.number().int().default(0),
    /** ADR-013: o TiFlux mudou de novo depois da prévia — nada decidido */
    stale: z.number().int().default(0),
  }),
  /** o ticket inteiro não foi processado (fora do espelho? do cliente? consulta falhou?) */
  error: z.string().nullable(),
});
export type TifluxPullTicketResult = z.infer<typeof TifluxPullTicketResultSchema>;

export const TifluxPullApplyResultSchema = z.object({
  tickets: z.array(TifluxPullTicketResultSchema),
});
export type TifluxPullApplyResult = z.infer<typeof TifluxPullApplyResultSchema>;

/** GET /tiflux/integration (admin + integrations.manage) */
export const TifluxIntegrationSchema = z.object({
  endpoint: z.string(),
  /** `****` + 4 últimos; null = nunca configurado */
  tokenMasked: z.string().nullable(),
  enabled: z.boolean(),
  intervalMinutes: z.number(),
  /** início da última rodada (lease do claim); null = nunca sincronizou */
  lastSyncAt: z.string().nullable(),
  /**
   * FIM da última rodada — carimbado junto do `lastResult`. É com ELE que a UI
   * detecta conclusão: `lastSyncAt` é gravado no COMEÇO (é o lease do claim) e
   * mudaria já no disparo, mostrando o resultado da rodada ANTERIOR; e
   * `syncRunning` é de processo (com N réplicas, a que responde o GET pode não
   * ser a que sincroniza). null = nenhuma rodada terminou ainda.
   */
  lastFinishedAt: z.string().nullable(),
  lastResult: TifluxSyncResultSchema.nullable(),
  /** rodada em voo NESTE processo — alimenta o "Sincronizando…" da UI */
  syncRunning: z.boolean(),
  /**
   * "Ocultar tickets espelhados" (spec 024, FR-006): ligada, ticket espelhado
   * de cliente que NÃO é operado no ERP some de toda leitura da tela — para
   * todos, admin inclusive — e o detalhe dele responde 404. O espelho segue
   * rodando; desligar volta a mostrar tudo. A API do bot não aplica.
   */
  hideMirrored: z.boolean(),
  /** spec 026: intervalo do ciclo dos clientes operados no ERP (padrão 10 min) */
  operatedIntervalMinutes: z.number(),
  /** começo da última volta do ciclo dos operados; null = nunca rodou */
  operatedLastSyncAt: z.string().nullable(),
  /** fim da última volta — gravado junto do `operatedLastResult` */
  operatedLastFinishedAt: z.string().nullable(),
  operatedLastResult: TifluxOperatedResultSchema.nullable(),
});
export type TifluxIntegration = z.infer<typeof TifluxIntegrationSchema>;

/**
 * PUT parcial: campos ausentes preservam o valor atual; na PRIMEIRA
 * configuração o token é obrigatório (422 token_required) — padrão
 * finance_integrations/ai_integrations. `endpoint` aceita http de propósito
 * (mock local nos smokes); o default aponta para a API real.
 */
export const TifluxIntegrationPutBodySchema = z.object({
  endpoint: z
    .string()
    .min(1)
    .refine((v) => v.startsWith('http://') || v.startsWith('https://'), 'must be an http(s) URL')
    .optional(),
  token: z.string().min(1).optional(),
  enabled: z.boolean().optional(),
  intervalMinutes: z
    .number()
    .int()
    .min(TIFLUX_MIN_INTERVAL_MINUTES)
    .max(TIFLUX_MAX_INTERVAL_MINUTES)
    .optional(),
  /** spec 024 (FR-006) — ausente preserva o valor atual, como o resto do PUT */
  hideMirrored: z.boolean().optional(),
  /** spec 026 (FR-002) — o ciclo dos clientes operados; ausente preserva */
  operatedIntervalMinutes: z
    .number()
    .int()
    .min(TIFLUX_MIN_INTERVAL_MINUTES)
    .max(TIFLUX_MAX_INTERVAL_MINUTES)
    .optional(),
});
export type TifluxIntegrationPutBody = z.infer<typeof TifluxIntegrationPutBodySchema>;

/** POST /tiflux/sync-now → 202 {started:true}; 409 sync_already_running */
export const TifluxSyncNowResponseSchema = z.object({ started: z.boolean() });
export type TifluxSyncNowResponse = z.infer<typeof TifluxSyncNowResponseSchema>;

// ── Clientes operados no ERP (spec 024, FR-003) ────────────────────────────
// Desligar um cliente do espelho é uma ação de admin em Configurações →
// TiFlux: todos os tickets ATUAIS dele saem do espelho (a marca "fora do
// espelho", motivo `client_detached`) e o cadastro dele deixa de ser
// reescrito. Não há volta pela tela — religar exigiria um "resync por
// cliente", fora do escopo; a reversão é manual.

const RefSchema = z.object({ id: z.number().int(), name: z.string() });

/** GET /tiflux/erp-clients — o registro do desligamento, que É a auditoria */
export const TifluxErpClientSchema = z.object({
  client: RefSchema,
  /** o vínculo do espelho no momento do desligamento; null = sem vínculo */
  tifluxClientId: z.number().int().nullable(),
  detachedAt: Dateish,
  /** quem desligou */
  detachedBy: RefSchema.nullable(),
  /** tickets que saíram do espelho no desligamento… */
  ticketsCount: z.number().int(),
  /** …e, desses, os que estavam abertos */
  openTicketsCount: z.number().int(),
});
export type TifluxErpClient = z.infer<typeof TifluxErpClientSchema>;

/** GET /tiflux/erp-clients/preview?client_id= — o que o diálogo de confirmação mostra */
export const TifluxErpClientPreviewQuerySchema = z.object({ client_id: IdCoerce });
export type TifluxErpClientPreviewQuery = z.infer<typeof TifluxErpClientPreviewQuerySchema>;

export const TifluxErpClientPreviewSchema = z.object({
  client: RefSchema,
  tifluxClientId: z.number().int().nullable(),
  /** todos os tickets do cliente hoje — os que o desligamento marcaria */
  tickets: z.number().int(),
  openTickets: z.number().int(),
  /**
   * Outros clientes com nome PARECIDO (uma palavra significativa em comum,
   * ou contida — MINAS × MINASCON). Tudo aqui é por id, nunca por nome: o
   * aviso existe para ninguém desligar o cliente errado.
   */
  similar: z.array(RefSchema),
  /** já desligado — a tela mostra o registro em vez do botão */
  alreadyDetached: z.boolean(),
});
export type TifluxErpClientPreview = z.infer<typeof TifluxErpClientPreviewSchema>;

/** POST /tiflux/erp-clients — 201 com o registro; 409 client_already_detached */
export const TifluxErpClientCreateBodySchema = z.object({ clientId: IdCoerce }).strict();
export type TifluxErpClientCreateBody = z.infer<typeof TifluxErpClientCreateBodySchema>;

/** códigos de erro das rotas → mensagem pt-BR (toasts do web) */
export const TIFLUX_ERROR_LABELS: Record<string, string> = {
  token_required: 'Informe o token de acesso da API TiFlux na primeira configuração.',
  tiflux_not_configured: 'Configure a integração TiFlux antes de sincronizar.',
  sync_already_running: 'Já existe uma sincronização em andamento.',
  client_not_found: 'Cliente não encontrado.',
  client_already_detached: 'Este cliente já é operado no ERP.',
  // spec 026 — "Puxar do TiFlux"
  client_not_operated: 'Este cliente não é operado no ERP: os tickets dele seguem o espelho do TiFlux.',
  operated_sync_busy: 'O ciclo dos clientes operados está rodando agora. Tente de novo em instantes.',
  tiflux_list_failed: 'Não foi possível consultar o TiFlux agora. Tente de novo em instantes.',
  tiflux_client_not_linked: 'Este cliente não tem vínculo com um cliente do TiFlux.',
};

/** rótulos pt-BR das entidades do lastResult (contadores/erros no card) */
export const TIFLUX_ENTITY_LABELS: Record<TifluxSyncEntity, string> = {
  desks: 'Mesas',
  clients: 'Clientes',
  users: 'Usuários',
  groups: 'Grupos de atendentes',
  entities: 'Campos personalizados',
  tickets: 'Tickets',
  comments: 'Comentários',
  appointments: 'Apontamentos',
  round: 'Rodada',
};
